import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle, Check, Package, CheckCircle } from 'lucide-react';
import Button from '../common/Button';
import LoadingSpinner from '../common/LoadingSpinner';
import { useUpdateRelease } from '../../api/hooks/updates/useReleaseMutation';
import { useCreateRollout } from '../../api/hooks/updates/useRolloutMutation';

interface ReleaseEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  release: any;
  onSuccess?: () => void;
}

export const ReleaseEditModal: React.FC<ReleaseEditModalProps> = ({
  isOpen,
  onClose,
  release,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    channel: release?.channel || 'stable',
    releaseNotes: release?.releaseNotes || '',
    minimumAppVersion: release?.minimumAppVersion || '',
    minimumOsVersion: release?.minimumOsVersion || '',
    isMandatory: release?.isMandatory || false,
    isDraft: release?.isDraft || false,
    rolloutPercentage: release?.rolloutPercentage || 100,
  });

  const updateRelease = useUpdateRelease();
  const createRollout = useCreateRollout();
  
  // Update form data when release changes
  useEffect(() => {
    if (release) {
      setFormData({
        channel: release.channel || 'stable',
        releaseNotes: release.releaseNotes || '',
        minimumAppVersion: release.minimumAppVersion || '',
        minimumOsVersion: release.minimumOsVersion || '',
        isMandatory: release.isMandatory || false,
        isDraft: release.isDraft || false,
        rolloutPercentage: release.rolloutPercentage || 100,
      });
    }
  }, [release]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Update the release metadata
      await updateRelease.mutateAsync({
        releaseId: release.id,
        data: formData,
      });
      
      // If the release is not a draft and rollout percentage is less than 100, create a new rollout
      if (!formData.isDraft && formData.rolloutPercentage !== 100) {
        await createRollout.mutateAsync({
          releaseId: release.id,
          data: {
            percentage: formData.rolloutPercentage,
          },
        });
      }
      
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error('Error updating release:', error);
    }
  };
  
  if (!isOpen || !release) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold">Edit Release</h2>
            <p className="text-sm text-gray-600 mt-1">
              {release.version} • {release.channel} • {release.platform} • {release.architecture}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Status Section */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Release Status</h3>
            <div className="space-y-3">
              {/* Draft */}
              <label className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-100 cursor-pointer">
                <div className="flex items-center gap-3">
                  <Package className="h-5 w-5 text-gray-500" />
                  <div>
                    <span className="font-medium">Draft</span>
                    <p className="text-sm text-gray-500">Release is not yet published</p>
                  </div>
                </div>
                <input
                  type="radio"
                  name="releaseStatus"
                  checked={formData.isDraft}
                  onChange={() => setFormData({
                    ...formData,
                    isDraft: true,
                    isMandatory: false
                  })}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                />
              </label>

              {/* Published - Optional */}
              <label className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-100 cursor-pointer">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-gray-500" />
                  <div>
                    <span className="font-medium">Published - Optional Update</span>
                    <p className="text-sm text-gray-500">Users can choose when to update</p>
                  </div>
                </div>
                <input
                  type="radio"
                  name="releaseStatus"
                  checked={!formData.isDraft && !formData.isMandatory}
                  onChange={() => setFormData({
                    ...formData,
                    isDraft: false,
                    isMandatory: false
                  })}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                />
              </label>

              {/* Published - Mandatory */}
              <label className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-100 cursor-pointer">
                <div className="flex items-center gap-3">
                  <AlertCircle className="h-5 w-5 text-gray-500" />
                  <div>
                    <span className="font-medium">Published - Mandatory Update</span>
                    <p className="text-sm text-gray-500">Users must update to continue using the app</p>
                  </div>
                </div>
                <input
                  type="radio"
                  name="releaseStatus"
                  checked={!formData.isDraft && formData.isMandatory}
                  onChange={() => setFormData({
                    ...formData,
                    isDraft: false,
                    isMandatory: true
                  })}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                />
              </label>
            </div>
          </div>

          {/* Metadata Section */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-3">Release Information</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Channel</label>
                <select
                  value={formData.channel}
                  onChange={(e) => setFormData({ ...formData, channel: e.target.value as 'stable' | 'beta' | 'alpha' })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                >
                  <option value="stable">Stable</option>
                  <option value="beta">Beta</option>
                  <option value="alpha">Alpha</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Release channel determines update availability
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Minimum App Version</label>
                <input
                  type="text"
                  value={formData.minimumAppVersion}
                  onChange={(e) => setFormData({ ...formData, minimumAppVersion: e.target.value })}
                  placeholder="0.9.0"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Minimum version required to apply this update
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Minimum OS Version</label>
                <input
                  type="text"
                  value={formData.minimumOsVersion}
                  onChange={(e) => setFormData({ ...formData, minimumOsVersion: e.target.value })}
                  placeholder={release?.platform === 'darwin' ? '10.15 (Catalina)' : '10 (Windows 10)'}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {release?.platform === 'darwin'
                    ? 'e.g., 10.15 for macOS Catalina, 11.0 for Big Sur'
                    : 'e.g., 10 for Windows 10, 11 for Windows 11'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Release Notes</label>
                <textarea
                  value={formData.releaseNotes}
                  onChange={(e) => setFormData({ ...formData, releaseNotes: e.target.value })}
                  rows={4}
                  placeholder="Bug fixes and improvements..."
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>
            </div>
          </div>

          {/* Rollout Configuration - Hidden (functionality preserved in backend) */}
          {/* Uncomment to re-enable rollout percentage UI */}
          {/*
          <div className="border-t pt-4">
            <button
              type="button"
              onClick={() => setShowRolloutConfig(!showRolloutConfig)}
              className="flex items-center justify-between w-full text-left"
              disabled={formData.isDraft}
            >
              <h3 className="text-lg font-medium text-gray-900">Rollout Configuration</h3>
              {showRolloutConfig ? (
                <ChevronUp className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-400" />
              )}
            </button>

            {showRolloutConfig && !formData.isDraft && (
              <div className="mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rollout Percentage
                  </label>
                  <div className="flex items-center space-x-4">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={formData.rolloutPercentage}
                      onChange={(e) => setFormData({ ...formData, rolloutPercentage: parseInt(e.target.value) })}
                      className="flex-1"
                    />
                    <div className="w-20">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={formData.rolloutPercentage}
                        onChange={(e) => setFormData({ ...formData, rolloutPercentage: parseInt(e.target.value) || 0 })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      />
                    </div>
                    <span className="text-sm text-gray-500">%</span>
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    {formData.rolloutPercentage === 100
                      ? 'This release will be available to all users immediately'
                      : `This release will be gradually rolled out to ${formData.rolloutPercentage}% of users`}
                  </p>
                </div>
              </div>
            )}

            {showRolloutConfig && formData.isDraft && (
              <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-md p-4">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> Rollout configuration is disabled for draft releases. Publish the release first to configure rollout settings.
                </p>
              </div>
            )}
          </div>
          */}

          {/* Error display */}
          {updateRelease.isError && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-800">
                {updateRelease.error?.message || 'Failed to update release'}
              </p>
            </div>
          )}
          
          {/* Success message */}
          {updateRelease.isSuccess && (
            <div className="bg-green-50 border border-green-200 rounded-md p-3 flex items-center gap-2">
              <Check className="h-4 w-4 text-green-600" />
              <p className="text-sm text-green-800">Release updated successfully!</p>
            </div>
          )}
          
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="secondary" onClick={onClose} disabled={updateRelease.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={updateRelease.isPending}>
              {updateRelease.isPending ? (
                <div className="flex items-center gap-2">
                  <LoadingSpinner />
                  <span>Saving...</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Save className="h-4 w-4" />
                  <span>Save Changes</span>
                </div>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};